// Staff users view - user management

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Staff users view component for managing users
 */
class StaffUsersView extends BaseComponent {
  static properties = {
    _users: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {Array<import('../../types/jsdoc-types.js').User>} */
    this._users = [];
    this._loading = true;
    this._error = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadUsers();
  }

  async _loadUsers() {
    this._loading = true;
    this._error = null;

    try {
      this._users = await api.get('/api/users');
    } catch (err) {
      console.error('Failed to load users:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load users';
    } finally {
      this._loading = false;
    }
  }

  /** @param {string} userId */
  async _makeAdmin(userId) {
    try {
      await api.patch(`/api/users/${userId}`, { rpgish_role: 'admin' });
      await this._loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to update user';
    }
  }

  /** @param {string | undefined} dateString */
  _formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  render() {
    return html`
      ${this._error ? html` <div class="alert alert-danger" role="alert">${this._error}</div> ` : ''}

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">All Users</h5>
        </div>
        <div class="card-body">
          ${
            this._loading
              ? html`
                <div class="text-center py-4">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2">Loading users...</p>
                </div>
              `
              : this._users.length === 0
                ? html` <p class="text-muted">No users yet.</p> `
                : html`
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Role</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${this._users.map(
                          (user) => html`
                            <tr>
                              <td>${user.username}</td>
                              <td>
                                <span class="badge ${user.rpgish_role === 'admin' ? 'bg-primary' : 'bg-secondary'}">
                                  ${user.rpgish_role}
                                </span>
                              </td>
                              <td>${this._formatDate(user.created_at)}</td>
                              <td>
                                ${
                                  user.rpgish_role === 'member'
                                    ? html`
                                      <button class="btn btn-sm btn-primary" @click="${() => this._makeAdmin(user.id)}">
                                        Make Admin
                                      </button>
                                    `
                                    : html`<span class="text-muted">-</span>`
                                }
                              </td>
                            </tr>
                          `,
                        )}
                      </tbody>
                    </table>
                  </div>
                `
          }
        </div>
      </div>
    `;
  }
}

customElements.define('staff-users-view', StaffUsersView);
